import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Bed, Bath, Maximize, MapPin, Car, Calendar } from 'lucide-react';

const PropertyCard = ({ property }) => {
  const { t, getLocalizedText, language } = useLanguage();

  const formatPrice = (price, priceType) => {
    // If price is already a string with currency symbol, return as-is
    if (typeof price === 'string' && (price.includes('$') || price.includes('€') || price.includes('UZS') || price.includes('₩'))) {
      return priceType === 'rent' ? `${price} ${t('perMonth')}` : price;
    }
    
    // Otherwise format as number
    const formatted = Number(price).toLocaleString();
    if (priceType === 'rent') {
      return `${formatted} ${t('perMonth')}`;
    }
    return `$${formatted}`;
  };

  const getBadgeVariant = (badge) => {
    switch (badge) {
      case 'premium': return 'bg-gradient-to-r from-amber-500 to-yellow-500 text-white';
      case 'hot': return 'bg-gradient-to-r from-red-500 to-orange-500 text-white';
      case 'new': return 'bg-gradient-to-r from-green-500 to-emerald-500 text-white';
      default: return 'bg-blue-100 text-blue-700';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'apartment': return t('apartments');
      case 'house': return t('houses');
      case 'commercial': return t('commercial');
      default: return type;
    }
  };

  // Support both mock data (id) and API data (property_id)
  const propertyId = property.property_id || property.id;
  const priceType = property.price_type || property.priceType;

  return (
    <Link to={`/property/${propertyId}`}>
      <Card className="group overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-500 bg-white rounded-2xl h-full">
        {/* Image Container */}
        <div className="relative h-56 overflow-hidden">
          <img
            src={property.images?.[0] || 'https://images.unsplash.com/photo-1515263487990-61b07816b324'}
            alt={getLocalizedText(property.title)}
            className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          
          {/* Badges */}
          <div className="absolute top-4 left-4 flex gap-2">
            {property.badge && (
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getBadgeVariant(property.badge)}`}>
                {t(property.badge)}
              </span>
            )}
            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-white/90 text-gray-700">
              {priceType === 'rent' ? t('forRent') : t('forSale')}
            </span>
          </div>
          
          {/* Price */}
          <div className="absolute bottom-4 left-4">
            <p className="text-2xl font-bold text-white drop-shadow-lg">
              {formatPrice(property.price, priceType)}
            </p>
          </div>
          
          {/* Property Type */}
          <div className="absolute bottom-4 right-4">
            <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-600/90 text-white">
              {getTypeLabel(property.type)}
            </span>
          </div>
        </div>

        <CardContent className="p-5">
          {/* Title */}
          <h3 className="font-semibold text-lg text-gray-900 mb-2 group-hover:text-blue-600 transition-colors line-clamp-1">
            {getLocalizedText(property.title)}
          </h3>
          
          {/* Address */}
          <div className="flex items-center gap-2 text-gray-500 mb-4">
            <MapPin className="w-4 h-4 text-blue-500" />
            <span className="text-sm line-clamp-1">{getLocalizedText(property.address)}</span>
          </div>
          
          {/* Features */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-100">
            {property.bedrooms > 0 && (
              <div className="flex items-center gap-1.5 text-gray-600">
                <Bed className="w-4 h-4 text-blue-500" />
                <span className="text-sm">{property.bedrooms}</span>
              </div>
            )}
            <div className="flex items-center gap-1.5 text-gray-600">
              <Bath className="w-4 h-4 text-blue-500" />
              <span className="text-sm">{property.bathrooms}</span>
            </div>
            <div className="flex items-center gap-1.5 text-gray-600">
              <Maximize className="w-4 h-4 text-blue-500" />
              <span className="text-sm">{property.area} {t('sqm')}</span>
            </div>
            {property.parking && (
              <div className="flex items-center gap-1.5 text-gray-600">
                <Car className="w-4 h-4 text-blue-500" />
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

export default PropertyCard;
